import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cx } from './cx'
import { focusControl } from './focus'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'icon'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | undefined
  readonly size?: ButtonSize | undefined
  readonly ref?: Ref<HTMLButtonElement> | undefined
  readonly children?: ReactNode
}

const base = cx(
  'inline-flex items-center justify-center gap-1.5 select-none whitespace-nowrap',
  'rounded-lg text-sm font-medium',
  'transition-[background-color,color,box-shadow,translate] duration-150 ease-out',
  focusControl,
  'disabled:cursor-not-allowed disabled:opacity-50',
)

const variants: Record<ButtonVariant, string> = {
  primary: cx(
    'bg-primary text-primary-foreground shadow-control',
    'enabled:hover:bg-primary/90 enabled:active:translate-y-px enabled:active:shadow-control-pressed',
  ),
  secondary: cx(
    'border border-border bg-background text-foreground shadow-control-outline',
    'enabled:hover:bg-muted enabled:active:translate-y-px enabled:active:shadow-control-pressed',
  ),
  ghost: 'bg-transparent text-foreground enabled:hover:bg-muted',
  danger: cx(
    'bg-destructive text-primary-foreground shadow-control',
    'enabled:hover:bg-destructive/90 enabled:active:translate-y-px enabled:active:shadow-control-pressed',
  ),
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3',
  md: 'h-9 px-4',
  icon: 'size-8 shrink-0 p-0',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      data-variant={variant}
      data-size={size}
      className={cx(base, variants[variant], sizes[size], className)}
      {...props}
    />
  )
}
